import React, { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

import {
  Alert,
  alpha,
  Box,
  CircularProgress,
  FormControlLabel,
  Paper,
  Switch,
  Typography,
  useTheme,
} from "@mui/material";

import DashboardIcon from "@mui/icons-material/Dashboard";
import BlockIcon from "@mui/icons-material/Block";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import StarIcon from "@mui/icons-material/Star";
import {
  getDeliveryCenterConfig,
  setDeliveryCenterConfig,
} from "../../../api/DeliveryCenter/deliveryCenter";
import {
  AnimatedModal,
  ModalContent,
  ModalHeader,
} from "../../modals/AnimatedModal";
import { dispatchSnackBarMessage } from "../../../utils/snackbar";
import MoneyTextField from "../../Inputs/MoneyTextField";

const configOptions = [
  // {
  //   id: "show_dashboard_auto_accept_button",
  //   title: "Botão de Auto-Aceite de Pedidos",
  //   description: {
  //     enabled:
  //       "Botão de auto-aceite de pedidos visível no painel (Somente use isso caso o aplicativo de impressão do Slapie esteja apresentando problemas)",
  //     disabled:
  //       "Botão de auto-aceite de pedidos oculto no painel (Somente use isso caso o aplicativo de impressão do Slapie esteja apresentando problemas)",
  //   },
  //   icon: TouchAppIcon,
  //   successMessage: (value) =>
  //     `Botão de auto-aceite de pedidos ${
  //       value ? "exibido" : "ocultado"
  //     } com sucesso`,
  //   errorMessage: "Erro ao atualizar configuração de auto-aceite",
  //   notificationId: "auto-accept-config",
  // },
  {
    id: "order_total_below_amount_alert",
    title: "Alerta de Pedido Abaixo do Valor Mínimo",
    description: {
      enabled:
        "Alerta ativado. Defina o valor mínimo abaixo. Pedidos com total inferior a este valor serão destacados.",
      disabled:
        "Alerta desativado. Nenhuma verificação de valor mínimo de pedido será realizada.",
    },
    icon: RequestQuoteIcon,
    hasMonetaryValue: true,
    defaultMonetaryValue: "0,00",
    successMessageOnEnable: (currentValue) =>
      `Alerta de valor mínimo de pedido ativado. Valor inicial: R$ ${currentValue}.`,
    successMessageOnDisable:
      "Alerta de valor mínimo de pedido desativado com sucesso.",
    successMessageOnValueChange: (newValue) =>
      `Valor mínimo do pedido para alerta atualizado para R$ ${newValue} com sucesso.`,
    errorMessage:
      "Erro ao atualizar configuração de alerta de valor mínimo do pedido.",
    notificationId: "order-total-below-amount-alert-config",
  },
  {
    id: "order_total_above_amount_alert",
    title: "Alerta de Pedido Especial (Valor Alto)",
    description: {
      enabled:
        "Alerta ativado. Defina o valor limite abaixo. Pedidos com total superior a este valor serão destacados como pedidos especiais.",
      disabled:
        "Alerta desativado. Nenhuma verificação de pedidos especiais de alto valor será realizada.",
    },
    icon: StarIcon,
    hasMonetaryValue: true,
    defaultMonetaryValue: "100,00",
    successMessageOnEnable: (currentValue) =>
      `Alerta de pedido especial (valor alto) ativado. Valor limite: R$ ${currentValue}.`,
    successMessageOnDisable:
      "Alerta de pedido especial (valor alto) desativado com sucesso.",
    successMessageOnValueChange: (newValue) =>
      `Valor limite para pedidos especiais atualizado para R$ ${newValue} com sucesso.`,
    errorMessage:
      "Erro ao atualizar configuração de alerta de pedidos especiais.",
    notificationId: "order-total-above-amount-alert-config",
  },
];

export default function DashboardScreenConfigsModal({ show, closeModal }) {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showError, setShowError] = useState(false);
  const [configValues, setConfigValues] = useState({});
  const [pendingMonetaryValues, setPendingMonetaryValues] = useState({});

  // Refs to store debounce timeouts for each monetary config
  const debounceTimeouts = useRef({});

  // Debounce delay in milliseconds
  const DEBOUNCE_DELAY = 800;

  useEffect(() => {
    if (!show) return;
    loadConfigs();
  }, [show]);

  // Cleanup debounce timeouts on unmount
  useEffect(() => {
    return () => {
      Object.values(debounceTimeouts.current).forEach(clearTimeout);
    };
  }, []);

  const loadConfigs = async () => {
    try {
      setLoading(true);
      setErrorMessage("");
      setShowError(false);

      const configParams = configOptions.map((option) => option.id).join(",");
      const params = { config: configParams };
      const { data } = await getDeliveryCenterConfig(params);

      setConfigValues(data || {});
      setPendingMonetaryValues({});
    } catch (err) {
      console.error(err);
      setErrorMessage(
        "Ocorreu um erro ao carregar as configurações. Tente novamente."
      );
      setShowError(true);
      dispatchSnackBarMessage(
        "configs-load-error",
        "Erro ao carregar configurações",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleConfigChange = async (configId, event) => {
    const isChecked = event.target.checked;
    const configOption = configOptions.find((option) => option.id === configId);

    if (!configOption) return;

    let newValueForApi;
    let successMsg;

    if (configOption.hasMonetaryValue) {
      if (isChecked) {
        newValueForApi =
          configValues[configId] || configOption.defaultMonetaryValue;
        if (
          configValues[configId] === null ||
          configValues[configId] === undefined
        ) {
          newValueForApi = configOption.defaultMonetaryValue;
        }
        successMsg = configOption.successMessageOnEnable(newValueForApi);
      } else {
        newValueForApi = null;
        successMsg = configOption.successMessageOnDisable;

        // Clear any pending debounced update for this config
        if (debounceTimeouts.current[configId]) {
          clearTimeout(debounceTimeouts.current[configId]);
          delete debounceTimeouts.current[configId];
        }
        setPendingMonetaryValues((prev) => {
          const newPending = { ...prev };
          delete newPending[configId];
          return newPending;
        });
      }
    } else {
      newValueForApi = isChecked;
      successMsg = configOption.successMessage(newValueForApi);
    }

    try {
      await setDeliveryCenterConfig({
        config: { [configId]: newValueForApi },
      });

      setConfigValues((prev) => ({
        ...prev,
        [configId]: newValueForApi,
      }));

      dispatchSnackBarMessage(
        configOption.notificationId,
        successMsg,
        "success"
      );
    } catch (e) {
      console.error(e);
      setConfigValues((prev) => ({
        ...prev,
        [configId]: configValues[configId],
      }));
      dispatchSnackBarMessage(
        `${configOption.notificationId}-error`,
        configOption.errorMessage,
        "error"
      );
    }
  };

  const debouncedMonetaryUpdate = useCallback(
    async (configId, monetaryValue) => {
      const configOption = configOptions.find(
        (option) => option.id === configId && option.hasMonetaryValue
      );

      if (!configOption) return;

      const oldValue = configValues[configId];

      try {
        await setDeliveryCenterConfig({
          config: { [configId]: monetaryValue },
        });

        // Update the actual config values after successful API call
        setConfigValues((prev) => ({
          ...prev,
          [configId]: monetaryValue,
        }));

        // Remove from pending values
        setPendingMonetaryValues((prev) => {
          const newPending = { ...prev };
          delete newPending[configId];
          return newPending;
        });

        dispatchSnackBarMessage(
          `${configOption.notificationId}-value-change`,
          configOption.successMessageOnValueChange(monetaryValue),
          "success"
        );
      } catch (e) {
        console.error(e);

        // Revert both actual and pending values on error
        setConfigValues((prev) => ({
          ...prev,
          [configId]: oldValue,
        }));
        setPendingMonetaryValues((prev) => {
          const newPending = { ...prev };
          delete newPending[configId];
          return newPending;
        });

        dispatchSnackBarMessage(
          `${configOption.notificationId}-update-error`,
          configOption.errorMessage,
          "error"
        );
      }
    },
    [configValues]
  );

  const handleMonetaryValueUpdate = (configId, monetaryValue) => {
    const configOption = configOptions.find(
      (option) => option.id === configId && option.hasMonetaryValue
    );

    if (!configOption) return;

    // Update pending values immediately for UI responsiveness
    setPendingMonetaryValues((prev) => ({
      ...prev,
      [configId]: monetaryValue,
    }));

    // Clear existing timeout for this config
    if (debounceTimeouts.current[configId]) {
      clearTimeout(debounceTimeouts.current[configId]);
    }

    // Set new timeout
    debounceTimeouts.current[configId] = setTimeout(() => {
      debouncedMonetaryUpdate(configId, monetaryValue);
      delete debounceTimeouts.current[configId];
    }, DEBOUNCE_DELAY);
  };

  const renderConfigItems = () => {
    return configOptions.map((configOpt) => {
      const configId = configOpt.id;
      const currentValue = configValues[configId];
      const pendingValue = pendingMonetaryValues[configId];

      // Use pending value if available, otherwise use current value
      const displayValue =
        pendingValue !== undefined ? pendingValue : currentValue;

      const isMonetaryType = configOpt.hasMonetaryValue;
      const isActive = isMonetaryType
        ? displayValue !== null && displayValue !== undefined
        : !!currentValue;

      const DisplayIcon = isActive ? configOpt.icon : BlockIcon;
      const hasPendingChanges = pendingValue !== undefined;

      return (
        <Paper
          key={configId}
          elevation={3}
          sx={{
            mb: 3,
            overflow: "hidden",
            borderRadius: 2,
            transition: "all 0.3s ease",
            border: "1px solid",
            borderColor: isActive
              ? alpha(theme.palette.success.main, 0.3)
              : alpha(theme.palette.warning.main, 0.3),
            opacity: hasPendingChanges ? 0.8 : 1,
          }}
        >
          <Box sx={{ position: "relative", overflow: "hidden" }}>
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                opacity: 0.1,
                background: isActive
                  ? `linear-gradient(45deg, ${theme.palette.success.light}, ${theme.palette.success.main})`
                  : `linear-gradient(45deg, ${theme.palette.warning.light}, ${theme.palette.warning.main})`,
                zIndex: 0,
              }}
            />

            <Box sx={{ position: "relative", zIndex: 1, p: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: alpha(
                      isActive
                        ? theme.palette.success.main
                        : theme.palette.warning.main,
                      0.1
                    ),
                    color: isActive
                      ? theme.palette.success.main
                      : theme.palette.warning.main,
                    borderRadius: 1.5,
                    p: 0.75,
                    width: 36,
                    height: 36,
                    mr: 2,
                  }}
                >
                  <DisplayIcon fontSize="small" />
                </Box>

                <Typography variant="h6" fontWeight={500}>
                  {configOpt.title}
                </Typography>

                <Box sx={{ ml: "auto" }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={isActive}
                        onChange={(e) => handleConfigChange(configId, e)}
                        color={isActive ? "success" : "warning"}
                        sx={{
                          "& .MuiSwitch-switchBase.Mui-checked": {
                            color: isActive
                              ? theme.palette.success.main
                              : theme.palette.warning.main,
                          },
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                            {
                              backgroundColor: isActive
                                ? theme.palette.success.main
                                : theme.palette.warning.main,
                            },
                        }}
                      />
                    }
                    label=""
                  />
                </Box>
              </Box>

              <Box sx={{ ml: 7, mb: 1 }}>
                <Typography
                  variant="subtitle1"
                  fontWeight={500}
                  color={isActive ? "success.main" : "warning.main"}
                >
                  {isActive ? "Ativado" : "Desativado"}
                  {hasPendingChanges && (
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{ ml: 1, fontStyle: "italic", opacity: 0.7 }}
                    >
                      (salvando...)
                    </Typography>
                  )}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {isActive
                    ? configOpt.description.enabled
                    : configOpt.description.disabled}
                </Typography>
              </Box>

              {isMonetaryType && isActive && (
                <Box sx={{ ml: 7, mt: 2, mb: 1 }}>
                  <MoneyTextField
                    id={`${configId}-value`}
                    label={
                      configId === "order_total_below_amount_alert"
                        ? "Valor Mínimo do Pedido"
                        : configId === "order_total_above_amount_alert"
                        ? "Valor Limite para Pedidos Especiais"
                        : "Valor"
                    }
                    value={displayValue}
                    setValue={(newVal) =>
                      handleMonetaryValueUpdate(configId, newVal)
                    }
                    prefix="R$"
                    fullWidth
                  />
                </Box>
              )}
            </Box>
          </Box>
        </Paper>
      );
    });
  };

  return (
    <AnimatedModal
      isOpen={show}
      onClose={closeModal}
      width="600px"
      maxWidth="90%"
    >
      <ModalHeader
        title="Configurações do Painel"
        icon={DashboardIcon}
        onClose={closeModal}
      />
      <ModalContent>
        <Box sx={{ px: 3, pt: 2, pb: 4 }}>
          <AnimatePresence>
            {showError && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <Alert
                  severity="error"
                  onClose={() => setShowError(false)}
                  sx={{ mb: 3, borderRadius: 2 }}
                >
                  {errorMessage}
                </Alert>
              </motion.div>
            )}
          </AnimatePresence>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            renderConfigItems()
          )}
        </Box>
      </ModalContent>
    </AnimatedModal>
  );
}
